import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import type { DTOUserLanguageEntry } from 'src/apis/dto/DTOUserLanguageEntry';
import IcList from 'src/assets/images/icListFill.svg';
import IcMinusCircleFill from 'src/assets/images/icMinusCircleFill.svg';
import { GridContextProvider, GridDropZone, GridItem, swap } from 'src/components/react-grid-drag';
import { GetPreferredLanguagesResponse } from 'src/stores/profile/apis';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
const ROW_HEIGHT = 52;
const SelectedWrap = styled(GridDropZone)`
  display: flex;
  flex-direction: column;
  user-select: none;
  touch-action: none;
`;
const PreferredLanguageOptionsWrap = styled.div`
  display: flex;
  flex-direction: column;
`;
const FiltersWrap = styled.div`
  display: flex;
  flex-direction: column;
`;
const FilterWrap = styled.div`
  display: flex;
  flex-direction: column;
`;
const FilterTitle = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${({
  theme
}) => theme.color.content12};
  padding-top: 20px;
  padding-bottom: 16px;
`;
const ItemCss = ({
  theme
}: {
  theme: Theme;
}) => css`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row;
  gap: 16px;
  color: ${theme.color.content12};
  border-color: ${theme.color.surface6};
  border-width: 0px;
  border-style: solid;
  padding: 16px 0;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
`;
const SelectedItem = styled(GridItem)``;
const SelectedItemInner = styled.div`
  ${ItemCss};
  border-bottom-width: 1px;
`;
const ListItem = styled.button`
  all: unset;
  ${ItemCss};
  & + & {
    border-top-width: 1px;
  }
  padding: 16px 0;
  &:disabled {
    cursor: default;
    color: ${({
  theme
}) => theme.color.content6};
  }
`;
const SelectedItemRemove = styled.button`
  all: unset;
  height: 20px;
  width: 20px;
`;
const SelectedItemRemoveIcon = styled(IcMinusCircleFill)`
  color: ${({
  theme
}) => theme.color.accentA1};
  width: 20px;
  height: 20px;
`;
const SelectedItemOrder = styled.div`
  color: ${({
  theme
}) => theme.color.content9};
`;
const SelectedItemLabel = styled.div`
  flex: 1;
`;
const SelectedItemHandle = styled(IcList)`
  width: 20px;
  height: 20px;
  padding: 12px;
  margin: -12px 0;
  color: ${({
  theme
}) => theme.color.content11};
`;
interface Props {
  draftSelectedPreferredLanguages: DTOUserLanguageEntry[];
  setDraftSelectedPreferredLanguages: Dispatch<SetStateAction<DTOUserLanguageEntry[]>>;
  preferredLanguagesData: GetPreferredLanguagesResponse;
}
const FreeFiltersContent: FC<Props> = ({
  draftSelectedPreferredLanguages,
  preferredLanguagesData,
  setDraftSelectedPreferredLanguages
}) => {
  const {
    t
  } = useTranslation();
  const draftPreferredLanguageCodeSet = useMemo(() => {
    return new Set(draftSelectedPreferredLanguages.map(preferredLanguage => preferredLanguage.code));
  }, [draftSelectedPreferredLanguages]);
  const handleClickPreferredLanguageOption = (preferredLanguage: DTOUserLanguageEntry) => {
    const isToggleOff = draftPreferredLanguageCodeSet.has(preferredLanguage.code);
    pushEvent({
      eventType: EVENT_TYPE.EDIT_PROFILE,
      eventName: EVENT_NAME.EDIT_PROFILE_MODAL__CLICK_PREFER_LANGUAGE,
      eventParams: {
        action_category: 'click',
        target: 'prefer_language',
        selected: preferredLanguage.code,
        toggle: isToggleOff ? 'off' : 'on'
      }
    });
    if (isToggleOff) {
      setDraftSelectedPreferredLanguages(prev => prev.filter(lang => lang.code !== preferredLanguage.code));
      return;
    }
    setDraftSelectedPreferredLanguages(prev => prev.concat([preferredLanguage]).slice(0, preferredLanguagesData.maxPreferredLanguageCount));
  };
  const handleDragEnd = (_sourceId: string, sourceIndex: number, targetIndex: number) => {
    if (draftSelectedPreferredLanguages.length) {
      setDraftSelectedPreferredLanguages(swap(draftSelectedPreferredLanguages, sourceIndex, targetIndex));
    }
  };
  return <FiltersWrap data-sentry-element="FiltersWrap" data-sentry-component="FreeFiltersContent" data-sentry-source-file="Content.tsx">
      <FilterWrap data-sentry-element="FilterWrap" data-sentry-source-file="Content.tsx">
        {draftSelectedPreferredLanguages.length > 0 && <>
            <FilterTitle>{t('profile_pref_lang_selected_sub_title')}</FilterTitle>
            <GridContextProvider onChange={handleDragEnd} horizontal={false}>
              <SelectedWrap boxesPerRow={1} id='edit-prefer-language-modal' rowHeight={ROW_HEIGHT}>
                {draftSelectedPreferredLanguages.map((option, i) => <SelectedItem key={`selected-preffer-languge-${option.code}`}>
                    <SelectedItemInner>
                      <SelectedItemRemove onClick={() => handleClickPreferredLanguageOption(option)}>
                        <SelectedItemRemoveIcon />
                      </SelectedItemRemove>
                      <SelectedItemOrder>{i + 1}</SelectedItemOrder>
                      <SelectedItemLabel>{option.displayName}</SelectedItemLabel>
                      <SelectedItemHandle />
                    </SelectedItemInner>
                  </SelectedItem>)}
              </SelectedWrap>
            </GridContextProvider>
          </>}

        <FilterTitle data-sentry-element="FilterTitle" data-sentry-source-file="Content.tsx">{t('profile_pref_lang_add_sub_title')}</FilterTitle>
        <PreferredLanguageOptionsWrap data-sentry-element="PreferredLanguageOptionsWrap" data-sentry-source-file="Content.tsx">
          {preferredLanguagesData.preferredLanguageOptions.filter(({
          code
        }) => !draftPreferredLanguageCodeSet.has(code)).map(option => <ListItem key={option.code} onClick={() => handleClickPreferredLanguageOption(option)} disabled={draftSelectedPreferredLanguages.length >= preferredLanguagesData.maxPreferredLanguageCount}>
                {option.displayName}
              </ListItem>)}
        </PreferredLanguageOptionsWrap>
      </FilterWrap>
    </FiltersWrap>;
};
export default FreeFiltersContent;